import type { $TSFixMe, GenericObject } from '@calefy-inc/utilityTypes';

// types and classes
import {
  QuoteWizardQuestionInstance,
  QuoteWizardAnswerInstance,
} from '../classes';
import Bugsnag from '@bugsnag/js';
import { errorify } from '../../../util';
import { FormValues } from '../../../Typescript/forms';

/**
 * Given the informed errors object, extract the full names as a string (including the position in an array or as part of an object). Basically - this is a complicated flatten function.
 */
export function extractFullNamesFromErrors(errors: $TSFixMe): Array<string> {
  const names: Array<string> = [];

  try {
    for (let [k, v] of Object.entries(errors)) {
      if (typeof v === 'string') {
        names.push(k);
      } else if (Array.isArray(v)) {
        // handle the array case here
        const constructedErrors = v.reduce((acc, entry, index) => {
          acc[`${k}[${index}]`] = entry;
          return acc;
        }, {});
        names.push(...extractFullNamesFromErrors(constructedErrors));
      } else if (typeof v === 'object') {
        // handle the object case here
        const constructedErrors: { [k: string]: any } = {};
        for (let [subK, subV] of Object.entries(v as $TSFixMe)) {
          constructedErrors[`${k}.${subK}`] = subV;
        }
        names.push(...extractFullNamesFromErrors(constructedErrors));
      }
    }

    return names;
  } catch (e) {
    console.error('Error extracting the full question name', e);
    Bugsnag.notify(errorify(e));
    return [];
  }
}

function orderAnswers(
  answerArray: Array<QuoteWizardAnswerInstance>,
  questionInstances: Array<QuoteWizardQuestionInstance>,
) {
  return answerArray.sort(
    (a1, a2) =>
      questionInstances.findIndex(
        (questionInstance) => a1.questionInstance.id === questionInstance.id,
      ) -
      questionInstances.findIndex(
        (questionInstance) => a2.questionInstance.id === questionInstance.id,
      ),
  );
}

/**
 * Convert the *values* of the FormState (informed) to the matching array of QuoteWizardAnswerInstances using the original set of questions from which the form was generated
 * @param formState - The informed FormState *values*
 * @param questionInstances - The original set of questions from which the form was generated
 * @return The array of QuoteWizardAnswerInstances
 */
export function formStateToAnswerInstances(
  formState: FormValues,
  questionInstances: Array<QuoteWizardQuestionInstance>,
): Array<QuoteWizardAnswerInstance> {
  //Takes an informed formState and iterates through it, creating
  //the related answerInstance array

  if (!formState) {
    formState = {};
  }
  let answerArray: Array<QuoteWizardAnswerInstance> = [];

  // Process all question instances, not just those in formState
  // This ensures that unchecked checkboxes and other missing fields are included
  questionInstances.forEach((questionInstance) => {
    const field = questionInstance.apiName;
    let value = formState[field];

    // If value is undefined and it's a checkbox, set it to false
    if (value === undefined && questionInstance.component === 'checkbox') {
      value = false;
    }

    // If value is still undefined, skip this question (it will be handled by amendAnswersListWithDefaultAnswers)
    if (value === undefined) {
      return;
    }

    // i.e. this is a repeatable set of questions
    if (Array.isArray(value)) {
      answerArray.push(
        ...value.map((eachInstantation, idx) => {
          return new QuoteWizardAnswerInstance({
            questionInstance: questionInstance,
            value: idx + 1,
            subAnswers: formStateToAnswerInstances(
              eachInstantation,
              questionInstance.subQuestions,
            ),
          });
        }),
      );
    } else {
      answerArray.push(
        new QuoteWizardAnswerInstance({
          questionInstance,
          value,
        }),
      );
    }
  });

  const ordered = orderAnswers(answerArray, questionInstances);
  //console.log({ ordered });
  return ordered;
}

const getAnswerWithDefaults = (q: QuoteWizardAnswerInstance) => {
  if (
    (q.value === 'N/A' || q.value === undefined) &&
    q.questionInstance.defaultValue
  ) {
    return q.questionInstance.defaultValue;
  }
  return q.value;
};

export function answerInstancesToFormState(
  answers: Array<QuoteWizardAnswerInstance> = [],
  questions: Array<QuoteWizardQuestionInstance> = [],
): $TSFixMe {
  let newFormState: GenericObject = {};
  for (let answer of answers) {
    if (!answer) {
      continue;
    }
    const matchingQuestion = questions.find((q) =>
      answer.matchQuoteWizardQuestionInstanceById(q),
    );
    if (!answer.subAnswers || answer.subAnswers.length === 0) {
      if (
        answer.questionInstance.subQuestions &&
        answer.questionInstance.subQuestions.length > 0
      ) {
        newFormState[answer.questionInstance.apiName] = [];
      } else {
        // newFormState[answer.questionInstance.apiName] = answer.value;
        newFormState[answer.questionInstance.apiName] =
          getAnswerWithDefaults(answer);
      }
    } else {
      if (
        newFormState &&
        newFormState.hasOwnProperty(answer.questionInstance.apiName)
      ) {
        newFormState[answer.questionInstance.apiName].push(
          answerInstancesToFormState(
            answer.subAnswers,
            matchingQuestion ? matchingQuestion.subQuestions : [],
          ),
        );
      } else {
        newFormState[answer.questionInstance.apiName] = [
          answerInstancesToFormState(
            answer.subAnswers,
            matchingQuestion ? matchingQuestion.subQuestions : [],
          ),
        ];
      }
    }
  }
  // now sort out any defaults for the answers *for which we don't already have answers*
  //console.log({ questions });
  questions
    .filter(
      (question) =>
        !answers.find((answer) =>
          answer.matchQuoteWizardQuestionInstanceById(question),
        ),
    )
    .forEach((unmatchedQuestion) => {
      //console.log('Processing', unmatchedQuestion);
      if (unmatchedQuestion.subQuestions.length === 0) {
        if (unmatchedQuestion.defaultValue) {
          newFormState[unmatchedQuestion.apiName] =
            unmatchedQuestion.defaultValue;
        }
      } else {
        if (newFormState.hasOwnProperty(unmatchedQuestion.apiName)) {
          try {
            newFormState[unmatchedQuestion.apiName].push(
              answerInstancesToFormState([], unmatchedQuestion.subQuestions),
            );
          } catch (error) {
            console.error(
              'answerInstancesToFormState push failed',
              unmatchedQuestion.apiName,
              newFormState[unmatchedQuestion.apiName],
              error,
            );
            Bugsnag.notify(errorify(error));
          }
        } else {
          newFormState[unmatchedQuestion.apiName] = [
            answerInstancesToFormState([], unmatchedQuestion.subQuestions),
          ];
        }
      }
    });
  return newFormState;
}
